import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HashLink } from 'react-router-hash-link';
import { 
  Eye, EyeOff, Mail, Lock, Building2, ArrowRight, Laptop, Shield, 
  Users, Sun, Moon, AlertCircle, CheckCircle, Github, Twitter, 
  Linkedin, Facebook, Instagram, HelpCircle, ExternalLink, Clock, Globe, User, ArrowLeft, 
  XCircle, Briefcase, Package2
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import TurnstileCaptcha from '../common/TurnstileCaptcha';
import { useAuth } from '../../hooks/useAuth.jsx';

// Supabase client initialization
const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

const LoginForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [emailError, setEmailError] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [dotCount, setDotCount] = useState(0);
  
  // Konum bilgisi için state
  const [locationInfo, setLocationInfo] = useState({ 
    isLoading: true, 
    city: "", 
    country: "",
    ip: "",
    textProgress: 0, // Yazı animasyonu için ilerleme
    stage: "waiting" // Konum belirleme aşaması: waiting, ip-search, location-search, complete
  });
  
  // Güvenlik kontrolleri için state'ler
  const [securityChecks, setSecurityChecks] = useState({
    userIdentity: { status: 'pending', message: 'Kullanıcı kimliği doğrulanıyor' },
    accountPermissions: { status: 'pending', message: 'Hesap yetkileri kontrolü' },
    multiSessionCheck: { status: 'pending', message: 'Çoklu oturum kontrolü' },
    sessionSecurity: { status: 'pending', message: 'Oturum güvenliği sağlanıyor' }
  });
  
  // Oturum işlem durumu
  const [authResult, setAuthResult] = useState(null); // null, success, error
  const [authMessage, setAuthMessage] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activePanel, setActivePanel] = useState('job'); // 'job' or 'stock'

  const { signIn } = useAuth();

  // Test database schema and login history logging
  const testLoginHistory = async () => {
    try {
      console.log('🧪 Testing login history system...');
      
      // Get current user session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ Session error:', sessionError);
        toast.error('Session hatası: ' + sessionError.message);
        return;
      }
      
      console.log('👤 Current session:', session);
      
      // Try to query the table first to see if it exists
      console.log('🔍 Checking if table exists...');
      const { data: existingRecords, error: queryError } = await supabase
        .from('user_login_history')
        .select('*')
        .limit(1);
        
      if (queryError) {
        console.error('❌ Table query failed:', queryError);
        console.error('❌ Error details:', {
          message: queryError.message,
          details: queryError.details,
          hint: queryError.hint,
          code: queryError.code
        });
        toast.error('Tablo sorgusu başarısız: ' + queryError.message);
        return;
      }
      
      console.log('📊 Table exists and queryable. Existing records:', existingRecords);
      toast.success('Tablo mevcut ve sorgulanabilir!');
      
      // Now test insert with current user ID or test UUID
      console.log('🔄 Testing insert...');
      const userId = session?.user?.id || '00000000-0000-0000-0000-000000000000';
      
      const testData = {
        user_id: userId,
        ip_address: '127.0.0.1',
        location: 'Test Location',
        user_agent: 'Test User Agent',
        success: true
        // error_message: null // Temporarily disabled - column doesn't exist
      };
      
      console.log('📝 Inserting test data:', testData);
      
      const { data: insertResult, error: insertError } = await supabase
        .from('user_login_history')
        .insert([testData])
        .select();
        
      if (insertError) {
        console.error('❌ Insert failed:', insertError);
        console.error('❌ Insert error details:', {
          message: insertError.message,
          details: insertError.details,
          hint: insertError.hint,
          code: insertError.code
        });
        toast.error('Insert başarısız: ' + insertError.message);
        return;
      }
      
      console.log('✅ Insert successful:', insertResult);
      toast.success('Login history test başarılı!');
      
      // Clean up test record
      await supabase
        .from('user_login_history')
        .delete()
        .eq('ip_address', '127.0.0.1')
        .eq('location', 'Test Location');
        
    } catch (error) {
      console.error('❌ Login history test failed:', error);
      toast.error('Login history test başarısız: ' + error.message);
    }
  };

  // Login history logging function
  const logLoginAttempt = async (userId, success, errorMessage = null) => {
    console.log('🔍 logLoginAttempt called:', { userId, success, errorMessage });
    try {
      // Get IP address and location
      const getLocationInfo = async () => {
        try {
          // Try multiple IP services
          const ipServices = [
            'https://api.ipify.org?format=json',
            'https://ipapi.co/json/',
            'https://ipinfo.io/json'
          ];
          
          let ipAddress = 'unknown';
          let location = 'Unknown';
          
          for (const service of ipServices) {
            try {
              const response = await fetch(service, { timeout: 3000 });
              if (response.ok) {
                const data = await response.json();
                ipAddress = data.ip || data.query || 'unknown';
                
                // Get location if we have IP
                if (ipAddress !== 'unknown') {
                  try {
                    const locationResponse = await fetch(`https://ipapi.co/${ipAddress}/json/`, { timeout: 3000 });
                    if (locationResponse.ok) {
                      const locationData = await locationResponse.json();
                      const city = locationData.city || 'Unknown';
                      const country = locationData.country_name || 'Unknown';
                      location = `${city}, ${country}`;
                    }
                  } catch (error) {
                    console.warn('Location service failed:', error);
                  }
                }
                break;
              }
            } catch (error) {
              continue;
            }
          }
          
          return { ip: ipAddress, location };
        } catch (error) {
          console.error('Location info error:', error);
          return { ip: 'unknown', location: 'Unknown' };
        }
      };

      // Get device info
      const getDeviceInfo = () => {
        const userAgent = navigator.userAgent;
        let deviceType = 'Desktop';
        let browser = 'Unknown';
        let os = 'Unknown';
        
        // Detect device type
        if (/Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
          deviceType = /iPad/i.test(userAgent) ? 'Tablet' : 'Mobile';
        }
        
        // Detect browser
        if (userAgent.includes('Chrome')) browser = 'Chrome';
        else if (userAgent.includes('Firefox')) browser = 'Firefox';
        else if (userAgent.includes('Safari')) browser = 'Safari';
        else if (userAgent.includes('Edge')) browser = 'Edge';
        else if (userAgent.includes('Opera')) browser = 'Opera';
        
        // Detect OS
        if (userAgent.includes('Windows')) os = 'Windows';
        else if (userAgent.includes('Mac')) os = 'macOS';
        else if (userAgent.includes('Linux')) os = 'Linux';
        else if (userAgent.includes('Android')) os = 'Android';
        else if (userAgent.includes('iOS')) os = 'iOS';
        
        return {
          deviceType,
          browser,
          os,
          userAgent,
          platform: navigator.platform,
          language: navigator.language,
          screenResolution: `${screen.width}x${screen.height}`,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          cookieEnabled: navigator.cookieEnabled,
          online: navigator.onLine
        };
      };

      const [{ ip, location }, deviceInfo] = await Promise.all([
        getLocationInfo(),
        Promise.resolve(getDeviceInfo())
      ]);

      const insertData = {
        user_id: userId,
        ip_address: ip,
        location,
        device_info: deviceInfo,
        user_agent: navigator.userAgent,
        success
        // error_message: success ? null : (errorMessage ? errorMessage.substring(0, 255) : null) // Temporarily disabled - column doesn't exist
      };
      
      console.log('📝 Inserting to user_login_history:', insertData);
      
      const { data, error } = await supabase
        .from('user_login_history')
        .insert([insertData])
        .select();
        
      if (error) {
        console.error('❌ user_login_history insert error:', error);
        throw error;
      }
      
      console.log('✅ user_login_history insert success:', data);
    } catch (error) {
      console.error('Login history logging failed:', error);
      // Don't throw - logging shouldn't break login flow
    }
  };

  // Logo URLs - tema uyumlu
  const logoUrls = {
    light: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent3.png", // Dark tema için beyaz logo
    dark: "https://vssrxrjuojnbslecwvzn.supabase.co/storage/v1/object/public/profile-photos/images_is_otomasyon/beetrax_logo_transparent4.png" // Light tema için siyah logo
  };
  
  // Mevcut tema durumuna göre logo URL'sini seç
  const currentLogo = isDark ? logoUrls.light : logoUrls.dark;
  
  // Yükleme mesajları
  const loadingMessages = [
    "Bağlantı kuruluyor, lütfen bekleyin",
    "Bilgileriniz doğrulanıyor, güvenliğiniz bizim için önemli",
    "Şifreleme protokolleri kontrol ediliyor",
    "Güvenlik kontrolleri yapılıyor, yetkisiz girişler engelleniyor",
    "Kimlik doğrulama işlemi tamamlanıyor",
    "Oturum bilgileri alınıyor, hesabınıza erişim sağlanıyor",
    "Kullanıcı yetkileri kontrol ediliyor, gerekli izinler tanımlanıyor",
    "Hesap ayarları hazırlanıyor, kişiselleştirilmiş deneyiminiz yükleniyor",
    "Kullanıcı verileri senkronize ediliyor",
    "Ön bellekteki bilgiler güncelleniyor",
    "Arka plan servisleri başlatılıyor, sistem stabil hale getiriliyor",
    "Son işlemler kaydediliyor, lütfen tarayıcıyı kapatmayın",
    "Panel yükleniyor, dinamik bileşenler başlatılıyor",
    "Sistem logları taranıyor, herhangi bir hata olup olmadığı kontrol ediliyor",
    "Güncellemeler yükleniyor, en yeni özelliklere erişiyorsunuz",
    "Son dokunuşlar yapılıyor, neredeyse hazırız!"
  ];
  

  // Animasyonlu üç nokta için
  useEffect(() => {
    if (!isLoading) return;
    
    const dotInterval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);
    
    return () => clearInterval(dotInterval);
  }, [isLoading]);

  // Mesaj değiştirme için
  useEffect(() => {
    if (!isLoading) return;
    
    const messageInterval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
    }, 3000);
    
    return () => clearInterval(messageInterval);
  }, [isLoading, loadingMessages.length]);

  // Güvenlik kontrolleri için işlem
  useEffect(() => {
    if (!isLoading) return;
    
    // Kullanıcı kimliği kontrolü
    const userIdentityCheck = setTimeout(() => {
      setSecurityChecks(prev => ({
        ...prev,
        userIdentity: { 
          ...prev.userIdentity, 
          status: 'completed'
        }
      }));
    }, 2000);
    
    // Hesap yetkileri kontrolü
    const accountPermissionsCheck = setTimeout(() => {
      setSecurityChecks(prev => ({
        ...prev,
        accountPermissions: { 
          ...prev.accountPermissions, 
          status: 'completed'
        }
      }));
    }, 4000);
    
    // Çoklu oturum kontrolü
    const multiSessionCheck = setTimeout(() => {
      setSecurityChecks(prev => ({
        ...prev,
        multiSessionCheck: { 
          ...prev.multiSessionCheck, 
          status: 'completed'
        }
      }));
    }, 6000);
    
    // Oturum güvenliği kontrolü
    const sessionSecurityCheck = setTimeout(() => {
      setSecurityChecks(prev => ({
        ...prev,
        sessionSecurity: { 
          ...prev.sessionSecurity, 
          status: 'completed'
        }
      }));
    }, 8000);
    
    return () => {
      clearTimeout(userIdentityCheck);
      clearTimeout(accountPermissionsCheck);
      clearTimeout(multiSessionCheck);
      clearTimeout(sessionSecurityCheck);
    };
  }, [isLoading]);
  
  // Oturum sonucu işleme - artık handleSubmit içinde yapılıyor
  // Önceki sayfa bilgisini korumak için lastLocation kullanılıyor
  /*
  useEffect(() => {
    if (authResult === null) return;
    
    const redirectTimeout = setTimeout(() => {
      if (authResult === 'success') {
        // Başarılı oturum açma: Seçili panele göre yönlendir
        if (activePanel === 'stock') {
          navigate('/stokflow');
        } else {
          navigate('/dashboard');
        }
      }
      // else kısmını kaldırdım - başarısız girişte modal kapanmayacak
    }, authResult === 'success' ? 5000 : 2000); // Başarılı girişte 5 saniye, hata durumunda 2 saniye
    
    return () => {
      clearTimeout(redirectTimeout);
    };
  }, [authResult, authMessage, navigate, activePanel]);
  */

  // Konum metni için ilerleme animasyonu
  useEffect(() => {
    // Konum henüz yüklenmemişse veya animasyon tamamlanmışsa çalışma
    if (locationInfo.isLoading || locationInfo.textProgress >= 100) return;
    
    // Konum belirlendiğinde yazı animasyonunu başlat
    const textAnimInterval = setInterval(() => {
      setLocationInfo(prev => ({
        ...prev,
        textProgress: Math.min(prev.textProgress + 2, 100)
      }));
    }, 20);
    
    return () => clearInterval(textAnimInterval);
  }, [locationInfo.isLoading, locationInfo.textProgress]);

  // Oturum modalı açıldığında konum bilgisini al
  useEffect(() => {
    if (!isLoading) return;
    
    const fetchLocationInfo = async () => {
      try {
        // Konum belirlenmeye başladığında ilerlemeyi sıfırla
        setLocationInfo(prev => ({
          ...prev,
          isLoading: true,
          textProgress: 0,
          stage: "waiting" // Başlangıç aşaması
        }));
        
        // İlk adım: IP adresi araması
        await new Promise(resolve => setTimeout(resolve, 500));
        setLocationInfo(prev => ({
          ...prev,
          stage: "ip-search" // IP arama aşaması
        }));
        
        // IP adresini alma seçenekleri - bir servis çalışmazsa diğerini dene
        let ipAddress = null;
        
        try {
          // 1. Yöntem: ipify.org
          const ipResponse = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipResponse.json();
          ipAddress = ipData.ip;
        } catch (error) {
          // console.log('ipify.org ile IP alınamadı, alternatif deneniyor...');
          
          try {
            // 2. Yöntem: icanhazip.com
            const response = await fetch('https://icanhazip.com');
            ipAddress = await response.text();
            ipAddress = ipAddress.trim();
          } catch (error2) {
            // console.log('Alternatif IP servisi de çalışmadı');
          }
        }
        
        if (!ipAddress) {
          throw new Error('IP adresi belirlenemedi');
        }
        
        // İkinci adım: Konum araması
        await new Promise(resolve => setTimeout(resolve, 400));
        setLocationInfo(prev => ({
          ...prev,
          stage: "location-search", // Konum arama aşaması
          ip: ipAddress // IP adresi bulundu
        }));
        
        // Konum bilgisini alma seçenekleri
        let locationData = null;
        
        try {
          // 1. Yöntem: ipapi.co
          const locationResponse = await fetch(`https://ipapi.co/${ipAddress}/json/`);
          locationData = await locationResponse.json();
          
          // Bazı ipapi.co hataları kontrol et
          if (locationData.error) {
            throw new Error('ipapi.co hata döndü');
          }
        } catch (error) {
          // console.log('ipapi.co ile konum alınamadı, alternatif deneniyor...');
          
          try {
            // 2. Yöntem: ip-api.com - CORS sorunları olabilir, ancak denemeye değer
            const locationResponse = await fetch(`https://ipwhois.app/json/${ipAddress}`);
            locationData = await locationResponse.json();
          } catch (error2) {
            // console.log('Alternatif konum servisi de çalışmadı');
          }
        }
        
        if (!locationData || locationData.error) {
          throw new Error('Konum bilgisi belirlenemedi');
        }
        
        // Üçüncü adım: Tamamlandı
        await new Promise(resolve => setTimeout(resolve, 300));
        setLocationInfo(prev => ({
          ...prev,
          stage: "complete" // Tamamlandı aşaması
        }));
        
        // En az 1.5 saniyelik bir yükleme süresi sağlamak için başlama zamanını kontrol et
        const startTime = Date.now();
        const minimumLoadingTime = 1500; // 1.5 saniye
        
        // Yeterli süre geçmiş mi kontrol et, geçmemişse bekle
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < minimumLoadingTime) {
          await new Promise(resolve => setTimeout(resolve, minimumLoadingTime - elapsedTime));
        }
        
        setLocationInfo({
          isLoading: false,
          city: locationData.city || "Bilinmiyor",
          country: locationData.country_name || locationData.country || "Bilinmiyor",
          ip: ipAddress,
          textProgress: 0, // İlerlemeyi sıfırlayarak animasyonu başlat
          stage: "complete" // Tamamlandı
        });
      } catch (error) {
        console.error('Konum belirleme hatası:', error);
        
        // Hata durumunda da minimum yükleme süresi sağla
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setLocationInfo({
          isLoading: false,
          city: "Belirlenemedi",
          country: "Belirlenemedi",
          ip: "Belirlenemedi",
          textProgress: 0, // İlerlemeyi sıfırlayarak animasyonu başlat
          stage: "error" // Hata durumu
        });
      }
    };
    
    fetchLocationInfo();
  }, [isLoading]);

  // E-posta doğrulama fonksiyonu
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const commonDomains = ['gmail.com', 'hotmail.com', 'yahoo.com', 'outlook.com', 'beetrax.net','urety.co','teira3d.com'];
    
    if (!email) {
      setEmailError('E-posta adresi gerekli');
      setIsEmailValid(false);
      return false;
    }
    
    if (!emailRegex.test(email)) {
      setEmailError('Geçerli bir e-posta adresi giriniz');
      setIsEmailValid(false);
      return false;
    }

    const domain = email.split('@')[1];
    if (!commonDomains.includes(domain) && !domain.endsWith('.edu.tr') && !domain.endsWith('.gov.tr')) {
      setEmailError('Yaygın olmayan bir domain kullanıyorsunuz, lütfen kontrol edin');
      setIsEmailValid(true); // Domain uyarısı olsa da format doğru
      return true;
    }

    setEmailError('');
    setIsEmailValid(true);
    return true;
  };

  // E-posta değiştiğinde kontrol et
  useEffect(() => {
    if (formData.email) {
      validateEmail(formData.email);
    }
  }, [formData.email]);

  // Güvenlik kontrollerinin tümü tamamlandı mı kontrol et
  const allSecurityChecksCompleted = () => {
    return Object.values(securityChecks).every(check => check.status === 'completed');
  };

  // CAPTCHA Callback Fonksiyonları
  const handleCaptchaVerify = (token) => {
    // console.log('CAPTCHA doğrulandı:', token);
    setCaptchaToken(token);
    setCaptchaError('');
  };

  const handleCaptchaExpire = () => {
    // console.log('CAPTCHA süresi doldu');
    setCaptchaToken(null);
    setCaptchaError('CAPTCHA süresi doldu. Lütfen tekrar doğrulayın.');
  };

  const handleCaptchaError = (error) => {
    console.error('CAPTCHA hatası:', error);
    setCaptchaToken(null);
    setCaptchaError('CAPTCHA yüklenirken bir hata oluştu. Lütfen sayfayı yenileyin.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Form gönderilmeden önce son bir kontrol
    if (!validateEmail(formData.email)) {
      return;
    }

    if (!formData.password) {
      toast.error('Şifre alanı boş bırakılamaz');
      return;
    }

    // CAPTCHA Token Kontrolü
    if (!captchaToken) {
      setCaptchaError('Lütfen CAPTCHA doğrulamasını tamamlayın');
      return;
    }

    // Yükleme modunu aktifleştir ve güvenlik kontrollerini sıfırla
    setIsLoading(true);
    setAuthResult(null);
    setAuthMessage("");
    setSecurityChecks({
      userIdentity: { status: 'pending', message: 'Kullanıcı kimliği doğrulanıyor' },
      accountPermissions: { status: 'pending', message: 'Hesap yetkileri kontrolü' },
      multiSessionCheck: { status: 'pending', message: 'Çoklu oturum kontrolü' },
      sessionSecurity: { status: 'pending', message: 'Oturum güvenliği sağlanıyor' }
    });

    // Kullanıcı bilgisini saklamak için değişken tanımlıyoruz
    let userExists = null;

    try {
      // Kullanıcının varlığını ve online_status değerini kontrol et (tek sorgu)
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, online_status, is_takip_permission, stok_takip_permission')
        .eq('email', formData.email)
        .single();

      if (error || !data) {
        // Login history'ye kaydet
        await logLoginAttempt('unknown', false, 'User not found');
        // Birkaç saniye bekleyip sonra hata göster
        await new Promise(resolve => setTimeout(resolve, 2000));
        setAuthResult('error');
        setAuthMessage('Bu email adresi ile kayıtlı bir kullanıcı bulunamadı');
        return;
      }
      userExists = data;

      // Seçili panel için yetki kontrolü
      if (activePanel === 'job' && userExists.is_takip_permission === false) {
        // Login history'ye kaydet
        await logLoginAttempt(userExists.id, false, 'No job tracking permission');
        await new Promise(resolve => setTimeout(resolve, 3000));
        setAuthResult('error');
        setAuthMessage('İş Takip sistemine erişim yetkiniz bulunmamaktadır');
        return;
      }

      if (activePanel === 'stock' && userExists.stok_takip_permission === false) {
        // Login history'ye kaydet
        await logLoginAttempt(userExists.id, false, 'No stock tracking permission');
        await new Promise(resolve => setTimeout(resolve, 3000));
        setAuthResult('error');
        setAuthMessage('Stok Takip sistemine erişim yetkiniz bulunmamaktadır');
        return;
      }

      // Rastgele gecikme süresi belirle (2-4 saniye arası)
      const randomDelay = Math.floor(Math.random() * (5000 - 2000 + 1)) + 500;

      // Belirtilen süre kadar bekle
      await new Promise(resolve => setTimeout(resolve, randomDelay));
      
      // useAuth hook'u ile CAPTCHA token'ı dahil giriş yap
      console.log('🔄 Calling signIn...');
      const signInResult = await signIn(formData.email, formData.password, captchaToken);
      console.log('✅ signIn result:', signInResult);
      
      // Başarılı giriş - login history useAuth'ta kaydediliyor, burada tekrar kaydetmiyoruz
      console.log('✅ Login successful - history will be logged by useAuth');
      
      // Başarılı giriş - authResult'ı güncelle
      setAuthResult('success');
      
      // Aktif panel bilgisini localStorage'a kaydet
      localStorage.setItem('activePanel', activePanel);
      
      // Timeout sonrası yönlendirme - önceki sayfaya dönüş kontrolü
      setTimeout(() => {
        // Önceki sayfaya dönüş kontrolü
        const lastLocation = localStorage.getItem('lastLocation');
        if (lastLocation) {
          // Önceki sayfa bilgisini kullan ve temizle
          localStorage.removeItem('lastLocation');
          navigate(lastLocation);
        } else {
          // Varsayılan yönlendirme
          if (activePanel === 'stock') {
            navigate('/stokflow');
          } else {
            navigate('/dashboard');
          }
        }
      }, 2000);
      
    } catch (error) {
      console.error('Login error:', error);
      
      // Login history useAuth'ta kaydediliyor, burada tekrar kaydetmiyoruz
      console.log('❌ Login failed - history will be logged by useAuth');
      
      // Hata mesajlarını Türkçeleştir
      let errorMessage = 'Giriş yapılırken bir hata oluştu';
      if (error.message && error.message.includes('Invalid login credentials')) {
        errorMessage = 'E-posta veya şifre hatalı';
      } else if (error.message && error.message.includes('Email not confirmed')) {
        errorMessage = 'E-posta adresi doğrulanmamış';
      }
      
      setAuthResult('error');
      setAuthMessage(errorMessage);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-gray-50'} py-12 px-4 sm:px-6 lg:px-8`}>
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`relative rounded-2xl px-8 py-10 ${isDark ? 'bg-slate-800/90' : 'bg-white/90'} shadow-2xl max-w-lg w-full mx-4 overflow-hidden`}
          >
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden">
              <div className={`absolute -top-20 -right-20 w-64 h-64 rounded-full ${
                isDark ? 'bg-blue-600/10' : 'bg-blue-500/5'
              } blur-3xl`}></div>
              <div className={`absolute -bottom-20 -left-20 w-64 h-64 rounded-full ${
                isDark ? 'bg-indigo-600/10' : 'bg-indigo-500/5'
              } blur-3xl`}></div>
              <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:16px_16px]"></div>
            </div>
            
            <div className="relative z-10 flex flex-col items-center">
              {/* Brand & Header */}
              <div className="w-full flex justify-center mb-6">
                <div className="relative">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className={`mb-2 px-4 py-1 rounded-full text-xs font-medium ${
                      isDark ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30' : 'bg-blue-100 text-blue-700 border border-blue-200'
                    }`}
                  >
                    Güvenli Oturum
                  </motion.div>
                </div>
              </div>
              
              {/* Main Loading Animation */}
              <div className="relative mb-8 flex justify-center">
                {authResult === 'success' ? (
                  // Başarılı giriş durumunda tik animasyonu
                  <motion.div 
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`flex items-center justify-center w-28 h-28 rounded-full ${
                      isDark ? 'bg-green-500/20' : 'bg-green-100'
                    }`}
                  >
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className={`flex items-center justify-center w-20 h-20 ${
                        isDark ? 'bg-green-600/30' : 'bg-green-200'
                      } rounded-full`}
                    >
                      <motion.div
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
                        className="w-12 h-12 text-green-500"
                      >
                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                ) : authResult === 'error' ? (
                  // Başarısız giriş durumunda X animasyonu
                  <motion.div 
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className={`flex items-center justify-center w-28 h-28 rounded-full ${
                      isDark ? 'bg-red-500/20' : 'bg-red-100'
                    }`}
                  >
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className={`flex items-center justify-center w-20 h-20 ${
                        isDark ? 'bg-red-600/30' : 'bg-red-200'
                      } rounded-full`}
                    >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ 
                          opacity: 1, 
                          scale: 1,
                          rotate: [0, 10, -10, 10, -10, 0],
                        }}
                        transition={{ 
                          duration: 0.5,
                          rotate: { duration: 0.8, delay: 0.2 },
                        }}
                        className="w-12 h-12 text-red-500"
                      >
                        <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </motion.div>
                    </motion.div>
                  </motion.div>
                ) : (
                  // Yükleme durumundaki dönen animasyon
                  <>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.div 
                        animate={{ 
                          rotate: 360,
                        }} 
                        transition={{ 
                          duration: 2, 
                          repeat: Infinity, 
                          ease: "linear" 
                        }}
                        className={`w-24 h-24 border-4 ${
                          isDark ? 'border-slate-700' : 'border-gray-200'
                        } rounded-full`}
                      ></motion.div>
                      
                      <motion.div 
                        animate={{ 
                          rotate: -360,
                        }} 
                        transition={{ 
                          duration: 3, 
                          repeat: Infinity, 
                          ease: "linear" 
                        }}
                        className="absolute w-20 h-20 border-4 border-blue-400 rounded-full border-t-transparent border-b-transparent"
                      ></motion.div>
                      
                      <motion.div 
                        animate={{ 
                          rotate: 360,
                        }} 
                        transition={{ 
                          duration: 4, 
                          repeat: Infinity, 
                          ease: "linear" 
                        }}
                        className="absolute w-16 h-16 border-4 border-indigo-400 rounded-full border-l-transparent border-r-transparent"
                      ></motion.div>
                    </div>
                    
                    <motion.div 
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: [0.8, 1, 0.8], opacity: 1 }}
                      transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                      className="flex items-center justify-center w-28 h-28 bg-gradient-to-br rounded-full"
                    >
                      <div className={`flex items-center justify-center w-16 h-16 ${
                        isDark ? 'bg-slate-800' : 'bg-white'
                      } rounded-full shadow-lg`}>
                        <Lock className={`w-8 h-8 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      </div>
                    </motion.div>
                  </>
                )}
              </div>
              
              {/* Title and Loading Text */}
              <h3 className={`text-2xl font-bold mb-1 text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {authResult === 'success' 
                  ? 'Oturum Başarılı' 
                  : authResult === 'error' 
                    ? 'Oturum Açılamadı' 
                    : 'Oturumunuz Açılıyor'}
              </h3>
              
              <div className="h-10 mb-6 flex items-center justify-center">
                <AnimatePresence mode="wait">
                  <motion.p 
                    key={authResult || loadingMessageIndex}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                    className={`text-center font-medium ${
                      isDark 
                        ? (authResult === 'error' ? 'text-red-400' : authResult === 'success' ? 'text-green-400' : 'text-gray-300') 
                        : (authResult === 'error' ? 'text-red-600' : authResult === 'success' ? 'text-green-600' : 'text-gray-600')
                    } ${!authResult ? 'animate-pulse' : ''}`}
                  >
                    {authResult === 'success'
                      ? 'Sizi sisteme yönlendiriyoruz, lütfen bekleyin...'
                      : authResult === 'error'
                        ? authMessage
                        : (loadingMessages[loadingMessageIndex] + 
                          (authResult ? '' : '.'.repeat(dotCount)))}
                  </motion.p>
                </AnimatePresence>
              </div>
              
              {/* Step Progress Indicator */}
              {!authResult && (
                <div className="w-full mb-6">
                  <div className="flex justify-between mb-2">
                    <span className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      İşlem: {loadingMessageIndex + 1}/{loadingMessages.length}
                    </span>
                    <span className={`text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      {Math.round((loadingMessageIndex + 1) / loadingMessages.length * 100)}%
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-200 rounded-full dark:bg-gray-700 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ 
                        width: `${((loadingMessageIndex + 1) / loadingMessages.length) * 100}%`
                      }}
                      className="h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600"
                    ></motion.div>
                  </div>
                </div>
              )}
              
              {/* Security Verification List */}
              {!authResult && (
                <div className="w-full mb-6">
                  <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Güvenlik Kontrolleri:
                  </h4>
                  <div className="space-y-2">
                    {[
                      { id: 'userIdentity', text: securityChecks.userIdentity.message, icon: <User className="w-4 h-4" />, status: securityChecks.userIdentity.status },
                      { id: 'accountPermissions', text: securityChecks.accountPermissions.message, icon: <Shield className="w-4 h-4" />, status: securityChecks.accountPermissions.status },
                      { id: 'multiSessionCheck', text: securityChecks.multiSessionCheck.message, icon: <Laptop className="w-4 h-4" />, status: securityChecks.multiSessionCheck.status },
                      { id: 'sessionSecurity', text: securityChecks.sessionSecurity.message, icon: <Lock className="w-4 h-4" />, status: securityChecks.sessionSecurity.status },
                    ].map((item) => (
                      <motion.div 
                        key={item.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex items-center"
                      >
                        <motion.div
                          initial={{ scale: 0.5, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.2 }}
                          className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
                            item.status === 'completed'
                              ? (isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700')
                              : (isDark ? 'bg-gray-700/50 text-gray-400' : 'bg-gray-200 text-gray-500')
                          }`}
                        >
                          {item.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                              className="w-3 h-3 border-2 border-current border-t-transparent rounded-full"
                            />
                          )}
                        </motion.div>
                        <div className="flex items-center">
                          <span className={`text-sm flex items-center ${
                            item.status === 'completed'
                              ? (isDark ? 'text-green-300' : 'text-green-600')
                              : (isDark ? 'text-gray-300' : 'text-gray-700')
                          }`}>
                            <span className="mr-2">{item.icon}</span>
                            {item.text}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Success or Error Message */}
              {authResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`w-full p-4 mb-6 rounded-xl ${
                    authResult === 'success'
                      ? (isDark ? 'bg-green-800/30 text-green-300 border border-green-700/30' : 'bg-green-50 text-green-700 border border-green-200')
                      : (isDark ? 'bg-red-800/30 text-red-300 border border-red-700/30' : 'bg-red-50 text-red-700 border border-red-200')
                  }`}
                >
                  <div className="flex items-center">
                    {authResult === 'success' ? (
                      <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                    ) : (
                      <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ 
                          scale: 1, 
                          opacity: 1,
                          rotate: [0, 10, -10, 10, -10, 0],
                        }}
                        transition={{ 
                          duration: 0.5,
                          rotate: { duration: 0.6, delay: 0.15 },
                        }}
                        className="mr-2 flex-shrink-0 text-red-500"
                      >
                        <XCircle className="w-6 h-6" />
                      </motion.div>
                    )}
                    <span>{authMessage}</span>
                  </div>
                </motion.div>
              )}
              
              {/* Hata durumunda "Vazgeç" butonu */}
              {authResult === 'error' && (
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  onClick={() => {
      setIsLoading(false);
                    setAuthResult(null);
                    setAuthMessage("");
                    setLoadingMessageIndex(0);
                    setDotCount(0);
                    setSecurityChecks({
                      userIdentity: { status: 'pending', message: 'Kullanıcı kimliği doğrulanıyor' },
                      accountPermissions: { status: 'pending', message: 'Hesap yetkileri kontrolü' },
                      multiSessionCheck: { status: 'pending', message: 'Çoklu oturum kontrolü' },
                      sessionSecurity: { status: 'pending', message: 'Oturum güvenliği sağlanıyor' }
                    });
                  }}
                  className={`w-full mb-6 py-3 px-4 rounded-xl ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  } transition-colors duration-200 font-medium flex items-center justify-center`}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Vazgeç
                </motion.button>
              )}
              
              {/* System Details */}
              <div className={`w-full p-3 rounded-xl text-xs ${
                isDark ? 'bg-slate-700/50 text-gray-300' : 'bg-gray-100 text-gray-600'
              } backdrop-blur-md`}>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-12">
                  <div className="flex items-center">
                    <Clock className="w-3 h-3 mr-1.5 opacity-70" />
                    <span>Tahmini süre: <strong>8-12</strong> sn</span>
                  </div>
                  <div className="flex items-center">
                    <Globe className="w-3 h-3 mr-1.5 opacity-70" />
                    <span className="truncate relative group">
                      Konum: {locationInfo.isLoading ? (
                        <motion.div 
                          initial={{ opacity: 1 }}
                          className="inline-flex flex-col"
                        >
                          {/* Durum yazısı - önce bu görünecek */}
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex items-center"
                          >
                            <motion.span
                              animate={{ opacity: [1, 0.6, 1] }}
                              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                              className="font-medium text-gray-400"
                            >
                              {locationInfo.stage === "waiting" && "Hazırlanıyor"}
                              {locationInfo.stage === "ip-search" && "IP Belirleniyor"}
                              {locationInfo.stage === "location-search" && "Konum Bulunuyor"}
                              {locationInfo.stage === "complete" && "Tamamlanıyor"}
                              {locationInfo.stage === "error" && "Belirlenemedi"}
                            </motion.span>
                            
                            <motion.span
                              animate={{ opacity: [1, 0.3, 1] }}
                              transition={{ duration: 0.5, repeat: Infinity, repeatType: "loop", times: [0, 0.5, 1] }}
                              className="inline-block ml-0.5 text-gray-400"
                            >
                              .
                            </motion.span>
                            <motion.span
                              animate={{ opacity: [1, 0.3, 1] }}
                              transition={{ duration: 0.5, repeat: Infinity, repeatType: "loop", delay: 0.2, times: [0, 0.5, 1] }}
                              className="inline-block text-gray-400"
                            >
                              .
                            </motion.span>
                            <motion.span
                              animate={{ opacity: [1, 0.3, 1] }}
                              transition={{ duration: 0.5, repeat: Infinity, repeatType: "loop", delay: 0.4, times: [0, 0.5, 1] }}
                              className="inline-block text-gray-400"
                            >
                              .
                            </motion.span>
                          </motion.div>
                          
                          {/* İkon ve ilerleme çubuğu - yazıdan sonra gelecek
                          
                          <motion.div 
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 0.5 }}
                            className="flex items-center mt-1.5"
                          >
                            Konum arama animasyonu - mini radar/pulse efekti
                            <motion.span 
                              className="relative inline-flex"
                              animate={{ opacity: [0.4, 1, 0.4] }}
                              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                            >
                              <span className={`absolute -left-1 -top-0.5 w-3 h-3 rounded-full ${
                                isDark 
                                  ? (locationInfo.stage === "error" ? 'bg-red-500/30' : 'bg-blue-500/30')
                                  : (locationInfo.stage === "error" ? 'bg-red-500/20' : 'bg-blue-500/20')
                              }`}></span>
                              <motion.span 
                                className={`absolute -left-1 -top-0.5 w-3 h-3 rounded-full ${
                                  isDark 
                                    ? (locationInfo.stage === "error" ? 'bg-red-500/10' : 'bg-blue-500/10')
                                    : (locationInfo.stage === "error" ? 'bg-red-500/10' : 'bg-blue-500/10')
                                }`}
                                animate={{ scale: [0, 1.5, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                              ></motion.span>
                              <Globe className={`w-2 h-2 ${
                                locationInfo.stage === "error" ? 'text-red-400' : 'text-blue-400'
                              }`} />
                            </motion.span>
                            
                              Mini yükleme çubuğu 
                            <motion.div 
                              className="h-1 w-12 bg-gray-600/30 rounded-full overflow-hidden ml-2"
                              style={{ fontSize: '0' }}
                            >
                              <motion.div
                                className={`h-full ${
                                  locationInfo.stage === "error" 
                                    ? 'bg-red-500/50' 
                                    : 'bg-blue-500/50'
                                }`}
                                animate={{
                                  width: ["0%", "100%", "0%"]
                                }}
                                transition={{
                                  duration: 3,
                                  ease: "easeInOut",
                                  repeat: Infinity,
                                }}
                              />
                            </motion.div>
                          </motion.div>
                          
                          
                          */}
                          
                        </motion.div>
                      ) : (
                        <AnimatePresence mode="wait">
                          <motion.span 
                            key={`${locationInfo.city}-${locationInfo.country}`}
                            initial={{ opacity: 0, filter: "blur(8px)" }}
                            animate={{ 
                              opacity: 1, 
                              filter: "blur(0px)",
                              transition: { 
                                duration: 0.5,
                                opacity: { duration: 0.3 },
                                filter: { duration: 0.5 }
                              }
                            }}
                            className="font-medium hover:text-blue-500 transition-colors cursor-help border-b border-dotted border-gray-400 inline-flex items-center"
                          >
                            {/* Konum bulundu işareti */}
                            <motion.span
                              initial={{ opacity: 0, scale: 0 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ 
                                type: "spring", 
                                stiffness: 500, 
                                damping: 15, 
                                delay: 0.7 
                              }}
                              className={`inline-flex items-center justify-center w-3 h-3 rounded-full ${
                                isDark ? 'bg-green-500/20' : 'bg-green-100'
                              } mr-1`}
                            >
                              <motion.span
                                initial={{ pathLength: 0 }}
                                animate={{ pathLength: 1 }}
                                transition={{ duration: 0.3, delay: 0.8 }}
                                className="w-2 h-2 text-green-500"
                              >
                                <svg className="w-full h-full" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M20 6L9 17l-5-5" />
                                </svg>
                              </motion.span>
                            </motion.span>
                            
                            {/* Konum bilgisi yazılıyormuş gibi görünen efekt */}
                            <span className="relative overflow-hidden">
                              <motion.span 
                                className="absolute inset-0 bg-blue-400/20 dark:bg-blue-500/30 blur-md"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ 
                                  opacity: [0, 0.8, 0], 
                                  scale: [0.8, 1.2, 2],
                                  transition: { duration: 1.5, delay: 0.2 }
                                }}
                              />
                              
                              {locationInfo.city !== "Bilinmiyor" && locationInfo.city !== "Belirlenemedi" 
                                ? (
                                  <>
                                    {`${locationInfo.city}, ${locationInfo.country}`.split('').map((char, i) => {
                                      const fullText = `${locationInfo.city}, ${locationInfo.country}`;
                                      const charPosition = (i / fullText.length) * 100;

  return (
                                        <motion.span
                                          key={`${char}-${i}`}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ 
                                            opacity: locationInfo.textProgress >= charPosition ? 1 : 0, 
                                            y: locationInfo.textProgress >= charPosition ? 0 : 10 
                                          }}
                                          transition={{ 
                                            duration: 0.05,
                                            ease: "easeOut"
                                          }}
                                          className="inline-block"
                                        >
                                          {char}
                                        </motion.span>
                                      );
                                    })}
                                  </>
                                )
                                : (
                                  <>
                                    {`${locationInfo.country}`.split('').map((char, i) => {
                                      const fullText = `${locationInfo.country}`;
                                      const charPosition = (i / fullText.length) * 100;
                                      
                                      return (
                                        <motion.span
                                          key={`${char}-${i}`}
                                          initial={{ opacity: 0, y: 10 }}
                                          animate={{ 
                                            opacity: locationInfo.textProgress >= charPosition ? 1 : 0, 
                                            y: locationInfo.textProgress >= charPosition ? 0 : 10 
                                          }}
                                          transition={{ 
                                            duration: 0.05,
                                            ease: "easeOut"
                                          }}
                                          className="inline-block"
                                        >
                                          {char}
                                        </motion.span>
                                      );
                                    })}
                                  </>
                                )
                              }
                            </span>
                          </motion.span>
                        </AnimatePresence>
                      )}
                      
                      {/* Konum detayları için tooltip */}
                      {!locationInfo.isLoading && (
                        <div className="absolute left-0 bottom-full mb-2 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                          <div className="font-semibold border-b border-gray-700 pb-1 mb-1">Konum Detayları</div>
                          <div className="space-y-1">
                            <div><span className="text-gray-400">IP:</span> {locationInfo.ip}</div>
                            <div><span className="text-gray-400">Şehir:</span> {locationInfo.city}</div>
                            <div><span className="text-gray-400">Ülke:</span> {locationInfo.country}</div>
                          </div>
                          <div className="absolute left-3 bottom-[-6px] w-3 h-3 bg-gray-800 transform rotate-45"></div>
                        </div>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Laptop className="w-3 h-3 mr-1.5 opacity-70" />
                    <span className="truncate">Cihaz: {/mobile/i.test(navigator.userAgent) ? 'Mobil' : 'Masaüstü'}</span>
                  </div>
                  <div className="flex items-center">
                    <Shield className="w-3 h-3 mr-1.5 opacity-70" />
                    <span>SSL: Güvenli Bağlantı</span>
                  </div>
                </div>
              </div>
              
              {/* Animated Particles */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(8)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-1.5 h-1.5 rounded-full ${
                      isDark ? 'bg-blue-500' : 'bg-blue-600'
                    } opacity-40`}
                    initial={{
                      x: Math.random() * 400 - 200,
                      y: Math.random() * 400 - 200,
                      scale: Math.random() * 0.5 + 0.5,
                    }}
                    animate={{
                      x: Math.random() * 400 - 200,
                      y: Math.random() * 400 - 200,
                      scale: Math.random() * 0.5 + 0.5,
                    }}
                    transition={{
                      duration: Math.random() * 10 + 10,
                      repeat: Infinity,
                      repeatType: "mirror",
                    }}
                  />
                ))}
              </div>
              
              {/* Bottom Message with Logo */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 1 }}
                className="absolute bottom-3 left-0 right-0 flex justify-center opacity-70"
              >
                <div className={`flex items-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  <span className={authResult ? '' : 'animate-pulse'}><strong>Beetrax</strong> güvenlik sistemi tarafından korunmaktadır</span>
                  <Shield className="w-3 h-3 ml-1" />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}

      <div className="absolute inset-0">
        <div className={`absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] ${isDark ? 'opacity-10' : 'opacity-20'}`}></div>
      </div>

      {/* Tema Değiştirme Butonu */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 p-2 rounded-full z-50 ${isDark ? 'bg-gray-800 text-yellow-400' : 'bg-white text-gray-800'} shadow-lg hover:scale-110 transition-all duration-200`}
      >
        {isDark ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
      </button>

      {/* Anasayfaya Geri Dön Butonu */}
      <button 
        onClick={() => navigate('/')}
        className={`fixed top-4 left-4 group flex items-center gap-1.5 px-3 py-2 rounded-lg z-50 ${
          isDark ? 'bg-gray-800/50 hover:bg-gray-700/70 text-gray-300' : 'bg-white/80 hover:bg-white text-gray-700'
        } backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-200`}
      >
        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
        <span className="text-sm font-medium ">Anasayfaya geri dön</span>
      </button>

      {/* Panel Toggle Button */}
      <div className="fixed bottom-4 md:top-4 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none">
        <div className={`relative flex p-1 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg backdrop-blur-sm border ${isDark ? 'border-gray-700' : 'border-gray-200'} pointer-events-auto`}>
          {/* Animated Background Slider */}
          <motion.div
            className="absolute inset-y-1 rounded-lg shadow-md z-0"
            initial={false}
            animate={{
              x: activePanel === 'job' ? 0 : '97%',
              width: '49%',
              backgroundColor: activePanel === 'job' 
                ? (isDark ? '#2563eb' : '#3b82f6') 
                : (isDark ? '#16a34a' : '#22c55e')
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              mass: 1
            }}
          />
          
          <button
            onClick={() => setActivePanel('job')}
            className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-300 ${
              activePanel === 'job' 
                ? 'text-white' 
                : (isDark 
                    ? 'text-gray-400 hover:text-gray-200' 
                    : 'text-gray-600 hover:text-gray-800')
            }`}
          >
            <Briefcase className="w-4 h-4" />
            <span>İş Takip</span>
          </button>
          <button
            onClick={() => setActivePanel('stock')}
            className={`relative z-10 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-300 ${
              activePanel === 'stock' 
                ? 'text-white' 
                : (isDark 
                    ? 'text-gray-400 hover:text-gray-200' 
                    : 'text-gray-600 hover:text-gray-800')
            }`}
          >
            <Package2 className="w-4 h-4" />
            <span>Stok Takip</span>
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-6xl mx-auto flex-grow"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-24">
          {/* Sol Taraf - Form */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activePanel}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
              className={`${isDark ? 'bg-white/10 backdrop-blur-xl border-white/10' : 'bg-white/80 backdrop-blur-xl border-gray-200'} p-8 lg:p-12 rounded-3xl border shadow-2xl`}
            >
              <div className="flex items-center space-x-3 mb-8">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                  className={`flex items-center justify-center w-12 h-12 rounded-xl ${
                    activePanel === 'job'
                      ? (isDark ? 'bg-blue-600/20 text-blue-400' : 'bg-blue-100 text-blue-600')
                      : (isDark ? 'bg-green-600/20 text-green-400' : 'bg-green-100 text-green-600')
                  }`}
                >
                  {activePanel === 'job' ? (
                    <Briefcase className="w-6 h-6" />
                  ) : (
                    <Package2 className="w-6 h-6" />
                  )}
                </motion.div>
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {activePanel === 'job' ? 'İş Takip Sistemi' : 'Stok Takip Sistemi'}
                </h2>
              </div>

              <motion.form
                onSubmit={handleSubmit}
                className="space-y-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                    E-posta
                  </label>
                  <div className="relative">
                    <Mail className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`w-full pl-12 pr-12 py-3 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} border rounded-xl focus:outline-none focus:ring-2 ${emailError && formData.email ? 'border-red-500 focus:ring-red-500' : isEmailValid ? 'border-green-500 focus:ring-green-500' : 'focus:ring-blue-500'} focus:border-transparent transition-all duration-200`}
                      placeholder="ornek@sirket.com"
                    />
                    {formData.email && (
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        {isEmailValid ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                    )}
                  </div>
                  {emailError && formData.email && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-2 text-sm ${isDark ? 'text-red-400' : 'text-red-600'} flex items-center`}
                    >
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {emailError}
                    </motion.p>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                    Şifre
                  </label>
                  <div className="relative">
                    <Lock className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className={`w-full pl-12 pr-12 py-3 ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-4 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'} transition-colors`}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => setFormData({ ...formData, rememberMe: e.target.checked })}
                      className={`h-4 w-4 ${isDark ? 'text-blue-500 border-white/10 bg-white/5' : 'text-blue-600 border-gray-300 bg-white'} rounded focus:ring-blue-500`}
                    />
                    <label className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      Beni hatırla
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password')}
                    className={`text-sm font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'} transition-colors`}
                  >
                    Şifremi unuttum
                  </button>
                </div>

                {/* Turnstile CAPTCHA */}
                <div className="space-y-2">
                  <TurnstileCaptcha
                    onVerify={handleCaptchaVerify}
                    onExpire={handleCaptchaExpire}
                    onError={handleCaptchaError}
                    action="login"
                    className="flex justify-center"
                    size="normal"
                  />
                  {captchaError && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-sm text-red-500 flex items-center justify-center"
                    >
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {captchaError}
                    </motion.p>
                  )}
                </div>

                <motion.button
                  whileHover={{ scale: captchaToken ? 1.01 : 1 }}
                  whileTap={{ scale: captchaToken ? 0.99 : 1 }}
                  type="submit"
                  disabled={!captchaToken}
                  className={`group relative w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-xl text-white font-medium shadow-lg transition-all duration-200 ${
                    !captchaToken 
                      ? 'opacity-50 cursor-not-allowed bg-gray-400' 
                      : activePanel === 'job' 
                        ? 'bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 focus:ring-blue-500' 
                        : 'bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 focus:ring-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                >
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <Lock className={`h-5 w-5 ${activePanel === 'job' ? 'text-blue-300 group-hover:text-blue-200' : 'text-green-300 group-hover:text-green-200'}`} aria-hidden="true" />
                  </span>
                  Oturum Aç
                  <ArrowRight className={`ml-2 w-5 h-5 ${activePanel === 'job' ? 'text-blue-300 group-hover:text-blue-200' : 'text-green-300 group-hover:text-green-200'} group-hover:translate-x-1 transition-transform`} />
                </motion.button>

                <p className={`text-center ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  Hesabınız yok mu?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/register')}
                    className={`font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'} transition-colors`}
                  >
                    Hemen kaydolun
                  </button>
                </p>

                {/* Debug: Test Login History Button */}
                {import.meta.env.DEV && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                     {/** 
                      <button
                      type="button"
                      onClick={testLoginHistory}
                      className="w-full px-4 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    >
                      🧪 Test Login History
                    </button>
                    */}
                  </div>
                )}
              </motion.form>
            </motion.div>
          </AnimatePresence>

          {/* Sağ Taraf - Özellikler */}
          <div className="hidden lg:flex flex-col justify-center space-y-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activePanel}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="text-center mb-12"
              >
                <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {activePanel === 'job' 
                    ? 'Operasyonlarınızı Daha Verimli Hale Getirin' 
                    : 'Stok Yönetimini Kolaylaştırın'}
                </h1>
                <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                  {activePanel === 'job'
                    ? 'İş süreçlerinizi tek bir platformdan yönetin, zaman kazanın ve verimliliği artırın.'
                    : 'Stok hareketlerinizi anlık takip edin, maliyetlerinizi düşürün ve satışlarınızı artırın.'}
                </p>
              </motion.div>
            </AnimatePresence>

            <AnimatePresence mode="wait">
              <motion.div
                key={activePanel}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
                className="grid grid-cols-1 gap-6"
              >
                {activePanel === 'job' ? (
                  // İş Takip Özellikleri
                  [
                    {
                      icon: Laptop,
                      title: 'Modern ve Esnek Arayüz Tasarımı',
                      description: 'İhtiyaçlarınıza uyum sağlayan kullanıcı dostu ve responsive tasarım.'
                    },
                    {
                      icon: Shield,
                      title: 'Üst Düzey Güvenlik',
                      description: 'Verilerinizi en güncel güvenlik standartlarıyla koruyun, kesintisiz erişim sağlayın.'
                    },
                    {
                      icon: Users,
                      title: 'Gerçek Zamanlı Ekip Yönetimi',
                      description: 'Ekibinizle anında iş birliği yapın, süreçleri kolayca yönetin, verimliliği artırın.'
                    }
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white/80 border-gray-200'} backdrop-blur-xl rounded-2xl p-6 border`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`${isDark ? 'bg-blue-500/10' : 'bg-blue-50'} rounded-lg p-3`}>
                          <feature.icon className={`w-6 h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
                            {feature.title}
                          </h3>
                          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Stok Takip Özellikleri 
                  [
                    {
                      icon: Package2,
                      title: 'Akıllı Stok Yönetimi',
                      description: 'Minimum stok seviyeleri, otomatik siparişler ve gerçek zamanlı envanter takibi.'
                    },
                    {
                      icon: Building2,
                      title: 'Çok Lokasyonlu Depo Yönetimi',
                      description: 'Farklı depolar arasında stok transferi, depo yerleşimleri ve alan optimizasyonu.'
                    },
                    {
                      icon: Globe,
                      title: 'Tedarik Zinciri Entegrasyonu',
                      description: 'Tedarikçiler ile entegrasyon, sipariş otomasyonu, teslimat takibi ve raporlama.'
                    }
                  ].map((feature, index) => (
                    <div
                      key={index}
                      className={`${isDark ? 'bg-white/5 border-white/10' : 'bg-white/80 border-gray-200'} backdrop-blur-xl rounded-2xl p-6 border`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`${isDark ? 'bg-green-500/10' : 'bg-green-50'} rounded-lg p-3`}>
                          <feature.icon className={`w-6 h-6 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`}>
                            {feature.title}
                          </h3>
                          <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Footer Bölümü */}
      <motion.footer
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className={`w-full mt-20 pb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`py-8 ${isDark ? 'border-t border-white/10' : 'border-t border-gray-200'}`}>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Şirket Bilgileri */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <img 
                    src={currentLogo} 
                    alt="Beetrax Logo" 
                    className="w-30 h-5 object-contain"
                  />
                </div>
                <p className="text-sm mb-4">
                  {activePanel === 'job' 
                    ? 'İş süreçlerinizi optimize eden, iş takip ve proje yönetimi çözümleriyle işletmenizi dijital dönüşüme taşıyan platform.'
                    : 'Stok yönetimi, tedarik zinciri ve envanter süreçlerini dijitalleştiren akıllı stok takip platformu.'}
                </p>
                <div className="flex space-x-4 mt-4">
                  <a 
                    href="#" 
                    className="transform transition-all duration-300 hover:scale-110 hover:rotate-6 p-2 rounded-full group relative overflow-hidden"
                    style={{ background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(239, 246, 255, 0.8)' }}
                  >
                    <span className={`absolute inset-0 bg-gradient-to-tr from-blue-500 to-indigo-600 opacity-0 group-hover:opacity-80 transition-opacity rounded-full transform scale-110`}></span>
                    <Github className={`w-5 h-5 relative z-10 group-hover:text-white transition-colors duration-300`} />
                  </a>
                  <a 
                    href="#" 
                    className="transform transition-all duration-300 hover:scale-110 hover:rotate-6 p-2 rounded-full group relative overflow-hidden"
                    style={{ background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(239, 246, 255, 0.8)' }}
                  >
                    <span className={`absolute inset-0 bg-gradient-to-tr from-blue-400 to-sky-500 opacity-0 group-hover:opacity-80 transition-opacity rounded-full transform scale-110`}></span>
                    <Twitter className={`w-5 h-5 relative z-10 group-hover:text-white transition-colors duration-300`} />
                  </a>
                  <a 
                    href="#" 
                    className="transform transition-all duration-300 hover:scale-110 hover:rotate-6 p-2 rounded-full group relative overflow-hidden"
                    style={{ background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(239, 246, 255, 0.8)' }}
                  >
                    <span className={`absolute inset-0 bg-gradient-to-tr from-blue-600 to-blue-800 opacity-0 group-hover:opacity-80 transition-opacity rounded-full transform scale-110`}></span>
                    <Linkedin className={`w-5 h-5 relative z-10 group-hover:text-white transition-colors duration-300`} />
                  </a>
                  <a 
                    href="#" 
                    className="transform transition-all duration-300 hover:scale-110 hover:rotate-6 p-2 rounded-full group relative overflow-hidden"
                    style={{ background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(239, 246, 255, 0.8)' }}
                  >
                    <span className={`absolute inset-0 bg-gradient-to-tr from-pink-500 to-purple-600 opacity-0 group-hover:opacity-80 transition-opacity rounded-full transform scale-110`}></span>
                    <Instagram className={`w-5 h-5 relative z-10 group-hover:text-white transition-colors duration-300`} />
                  </a>
                  <a 
                    href="#" 
                    className="transform transition-all duration-300 hover:scale-110 hover:rotate-6 p-2 rounded-full group relative overflow-hidden"
                    style={{ background: isDark ? 'rgba(30, 41, 59, 0.5)' : 'rgba(239, 246, 255, 0.8)' }}
                  >
                    <span className={`absolute inset-0 bg-gradient-to-tr from-blue-700 to-blue-900 opacity-0 group-hover:opacity-80 transition-opacity rounded-full transform scale-110`}></span>
                    <Facebook className={`w-5 h-5 relative z-10 group-hover:text-white transition-colors duration-300`} />
                  </a>
                </div>
              </div>
              
              {/* Hızlı Bağlantılar */}
              <div>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 uppercase tracking-wider`}>
                  Hızlı Bağlantılar
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a 
                      href="/welcome" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Ana Sayfa
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-blue-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="/welcome#features" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Özellikler
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-blue-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="/welcome#pricing" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Fiyatlandırma
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-blue-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="/welcome#about" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Hakkımızda
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-blue-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="/blog" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Blog
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-blue-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
              
              {/* Yasal */}
              <div>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 uppercase tracking-wider`}>
                  Yasal
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a 
                      href="privacy-policy" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Gizlilik Politikası
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-gray-500 to-gray-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="terms-of-service" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Kullanım Koşulları
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-gray-500 to-gray-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="kvkk" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        KVKK
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-gray-500 to-gray-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="#" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className="p-1 mr-2 rounded-full bg-gradient-to-r from-gray-500 to-gray-600 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                        <ArrowRight className="w-3 h-3 text-white" />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Çerez Politikası
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-gray-500 to-gray-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
              
              {/* İletişim */}
              <div>
                <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 uppercase tracking-wider`}>
                  Destek
                </h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <a 
                      href="#" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className={`flex items-center justify-center w-6 h-6 mr-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-100'} group-hover:bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-300`}>
                        <HelpCircle className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'} group-hover:text-white transition-colors duration-300`} />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-purple-400' : 'text-purple-600'} transition-colors duration-200`}>
                        Yardım Merkezi
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-purple-500 to-purple-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="mailto:info@beetrax.net" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className={`flex items-center justify-center w-6 h-6 mr-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-100'} group-hover:bg-gradient-to-r from-green-500 to-green-600 transition-all duration-300`}>
                        <Mail className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'} group-hover:text-white transition-colors duration-300`} />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-green-400' : 'text-green-600'} transition-colors duration-200`}>
                        info@beetrax.net
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-green-500 to-green-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                  <li>
                    <a 
                      href="#" 
                      className="group flex items-center transform transition-all duration-200 hover:translate-x-1"
                    >
                      <span className={`flex items-center justify-center w-6 h-6 mr-2 rounded-full ${isDark ? 'bg-gray-800' : 'bg-gray-100'} group-hover:bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300`}>
                        <ExternalLink className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'} group-hover:text-white transition-colors duration-300`} />
                      </span>
                      <span className={`relative group-hover:${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors duration-200`}>
                        Canlı Destek
                        <span className="absolute left-0 bottom-0 h-0.5 w-0 bg-gradient-to-r from-blue-500 to-blue-600 group-hover:w-full transition-all duration-300"></span>
                      </span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Telif Hakkı */}
            <div className={`mt-12 pt-6 ${isDark ? 'border-t border-white/10' : 'border-t border-gray-200'} flex flex-col md:flex-row justify-between items-center`}>
              <p className="text-sm mb-4 md:mb-0">
                &copy; {new Date().getFullYear()} {activePanel === 'job' ? 'İş Takip' : 'Stok Takip'} Sistemi. Tüm Hakları Saklıdır.
              </p>
              <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <span className="inline-flex items-center">
                  Güvenli ve modern {activePanel === 'job' ? 'iş' : 'stok'} yönetimi <sup>®</sup>
                </span>
              </div>
            </div>
          </div>
        </div>
      </motion.footer>
    </div>
  );
};

export default LoginForm;
